import React, { useState, useEffect } from 'react';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import Toast from '../../components/common/Toast';
import './NationalSetting.css';
import api from '../../api/axios';
import { useMainCompany } from '../../context/MainCompanyContext';
import { Save } from 'lucide-react';

const SAMPLE = {
  fha_closing_costs: {
    loan_amount_factor: {
      ranges: [
        { label: 'under', sale_price: 50000, ltv_percent: 96.5 },
        { label: 'to', sale_price: 125000, ltv_percent: 96.5 },
        { label: 'over', sale_price: 125000, ltv_percent: 96.5 }
      ],
      refinance_ltv_percent: 97.75,
      origination_factor_percent: 1
    },
    mmi_factor_setting: [
      { factor: 0.55, up_to_amount: 726200 },
      { factor: 0.75, exceeding_from_amount: 726200.01 }
    ],
    mortgage_insurance: {
      years: [
        { years: 30, percent: 1.75, finance_mip: true, round_down_mip: false },
        { years: 15, percent: 1.75, finance_mip: false, round_down_mip: true }
      ]
    }
  },
  va_closing_costs: {
    loan_amount_factor: { origination_factor_percent: 1, maximum_100_percent_va_loan: 417000 },
    mortgage_insurance: { finance_mip: true, round_down_mip: false, funding_fee_percent: 2.15 }
  },
  usda_closing_costs: {
    loan_amount_factor: { finance_mip: true, mip_factor_percent: 0.01, monthly_mip_factor_percent: 0.35, origination_factor_percent: 1, maximum_100_percent_usda_loan: 500000 }
  },
  conventional_loans: {
    pmi_rates_15_year: { unit: 'percent', title: 'Private Mortgage Insurance (P.M.I) Rates For 15 Year', renewal_rates_percent: { '95_to_97':1.23,'80_1_to_85':0.21,'85_1_to_90':0.31,'90_1_to_95':0.71 }, months_collected_at_closing: 2 },
    pmi_rates_30_year: { unit: 'percent', title: 'Private Mortgage Insurance (P.M.I) Rates For 30 Year', renewal_rates_percent: { '95_to_97':1.31,'80_1_to_85':0.58,'85_1_to_90':0.78,'90_1_to_95':0.98 }, months_collected_at_closing: 2 },
    conventional_loan_defaults: { resale_conventional_loan_ltv_percent:90, refinance_conventional_loan_ltv_percent:80 }
  }
};

function SectionCard({ title, children, onSave }){
  return (
    <div className="bg-white shadow-md rounded-xl p-4 border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-800 text-base">{title}</h3>
        <button className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm" onClick={onSave}><Save size={14} /> Save</button>
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function NationalSetting(){
  const { selectedMain } = useMainCompany();
  const [activeTab, setActiveTab] = useState('FHAVA');
  const [data, setData] = useState(SAMPLE);
  const [toast, setToast] = useState(null);
 // const [stateId] = useState(0);

  // helper to update nested keys for a given section
  function updateSectionField(sectionKey, keyPath, value){
    setData(prev => {
      const copy = JSON.parse(JSON.stringify(prev || {}));
      let node = copy[sectionKey] = copy[sectionKey] || {};
      for (let i=0;i<keyPath.length-1;i++){
        const k = keyPath[i];
        if (node[k] === undefined || node[k] === null) node[k] = {};
        node = node[k];
      }
      node[keyPath[keyPath.length-1]] = value;
      return copy;
    });
  }
      useEffect(()=>{ if (!toast) return; const t = setTimeout(()=>setToast(null), 2500); return ()=>clearTimeout(t); },[toast]);
  useEffect(()=>{ if (!toast) return; const t = setTimeout(()=>setToast(null), 2500); return ()=>clearTimeout(t); },[toast]);

  // fetch national setting for selectedMain
  useEffect(()=>{
    async function load(){
      if (!selectedMain) return;
      try{
        const params = { maincompanyid: selectedMain };
        const resp = await api.get('/admin/national-setting', { params });
        const body = resp.data ?? resp;
        const rows = body && Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : []);
        if (rows && rows.length>0){
          setData(rows[0]);
        } else {
          // keep sample as fallback
          setData(SAMPLE);
        }
      }catch(err){
        console.error('fetch national setting', err);
        setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to load national setting' });
      }
    }
    load();
  },[selectedMain]);

  // save a single section (PUT)
  // helper: get nested value by path from an object
  const getNested = (obj, path) => {
    if (!obj || !path || path.length === 0) return undefined;
    return path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
  };

  // helper: format label text (replace underscores and capitalize first char)
  const formatLabel = (input) => {
    if (input === null || input === undefined) return '';
    let s = String(input);
    s = s.replace(/_/g, ' ');
    return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  };

  // helper: build nested object from path and value, e.g. ['a','b'] + v => { a: { b: v } }
  const buildNested = (path, value) => {
    if (!path || path.length === 0) return value;
    const [head, ...rest] = path;
    if (rest.length === 0) return { [head]: value };
    return { [head]: buildNested(rest, value) };
  };

  /**
   * Save a section or a nested node inside a section.
   * - sectionKey: top-level JSONB column name (e.g. 'fha_closing_costs')
   * - nodePath: optional array describing nested node to save (e.g. ['loan_amount_factor'])
   * If nodePath is provided, the payload will include only that node inside the section.
   */
  const saveSection = async (sectionKey, nodePath = null, sectionTitle = null) => {
    if (!data || !data.id) return setToast({ type: 'error', message: 'No setting to update' });
    try {
      const sectionRoot = data[sectionKey] || {};
      const sectionPayload = nodePath && nodePath.length ? buildNested(nodePath, getNested(sectionRoot, nodePath)) : sectionRoot;

      const payload = {
        maincompanyid: Number(selectedMain) || Number(data.maincompanyid) || 0,
        [sectionKey]: sectionPayload,
      };

      const resp = await api.put(`/admin/national-setting/${data.id}`, payload);
      const body = resp.data ?? resp;
  setToast({ type: 'success', message: body?.message || `${sectionTitle || formatLabel(sectionKey)} is updated successfully` });

      // refresh the record
      const refreshed = await api.get('/admin/national-setting', { params: { maincompanyid: selectedMain } });
      const rows = refreshed.data && Array.isArray(refreshed.data.data) ? refreshed.data.data : (Array.isArray(refreshed.data) ? refreshed.data : []);
      if (rows && rows.length > 0) setData(rows[0]);
    } catch (err) {
      console.error('save failed', err);
      setToast({ type: 'error', message: err?.response?.data?.message || 'Save failed' });
    }
  };

  return (
    <div className="p-4 space-y-4 national-setting-page-root">
      <AdminPageHeader title="National Setting" />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setActiveTab('FHAVA')} className={`px-3 py-1 rounded-full text-sm font-medium ${activeTab==='FHAVA' ? 'bg-blue-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>FHA/VA/USDA Loans</button>
        <button onClick={() => setActiveTab('CONV')} className={`px-3 py-1 rounded-full text-sm font-medium ${activeTab==='CONV' ? 'bg-blue-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>Conventional Loans</button>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {activeTab === 'FHAVA' ? (
          <>
            <SectionCard title="FHA Loan Amount Factor" onSave={() => saveSection('fha_closing_costs', ['loan_amount_factor'], 'FHA Loan Amount Factor')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  {Array.isArray(data.fha_closing_costs.loan_amount_factor.ranges) && data.fha_closing_costs.loan_amount_factor.ranges.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 border">{formatLabel(r.label)}</td>
                      <td className="px-3 py-2 border input-cell"><input type={typeof (r.sale_price) === 'number' ? 'number' : 'text'} className="w-full border rounded px-2 py-1 text-sm" value={r.sale_price ?? ''} onChange={e => updateSectionField('fha_closing_costs', ['loan_amount_factor','ranges',i,'sale_price'], Number(e.target.value))} /></td>
                      <td className="px-3 py-2 border input-cell"><input type={typeof (r.ltv_percent) === 'number' ? 'number' : 'text'} className="w-full border rounded px-2 py-1 text-sm" value={r.ltv_percent ?? ''} onChange={e => updateSectionField('fha_closing_costs', ['loan_amount_factor','ranges',i,'ltv_percent'], Number(e.target.value))} /></td>
                    </tr>
                  ))}
                  <tr>
                    <td className="px-3 py-2 border">Origination Factor %</td>
                    <td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={data.fha_closing_costs.loan_amount_factor.origination_factor_percent ?? ''} onChange={e => updateSectionField('fha_closing_costs',['loan_amount_factor','origination_factor_percent'], Number(e.target.value))} /></td>
                    {/* <td className="px-3 py-2 border">FHA Refinance LTV% <input className="w-24 border rounded px-2 py-1 text-sm ml-2" value={data.fha_closing_costs.loan_amount_factor.refinance_ltv_percent ?? ''} onChange={e => updateSectionField('fha_closing_costs',['loan_amount_factor','refinance_ltv_percent'], Number(e.target.value))} /></td> */}
                  </tr>
                  
                  <tr>
                    <td className="px-3 py-2 border">FHA Refinance LTV%</td>
                    <td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={data.fha_closing_costs.loan_amount_factor.refinance_ltv_percent ?? ''} onChange={e => updateSectionField('fha_closing_costs',['loan_amount_factor','refinance_ltv_percent'], Number(e.target.value))} /></td>
                    
                  </tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="FHA Mortgage Insurance" onSave={() => saveSection('fha_closing_costs', ['mortgage_insurance'], 'FHA Mortgage Insurance')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  {Array.isArray(data.fha_closing_costs.mortgage_insurance.years) ? data.fha_closing_costs.mortgage_insurance.years.map((y, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 border">Years</td>
                      <td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={y.years ?? ''} onChange={e => updateSectionField('fha_closing_costs',['mortgage_insurance','years',i,'years'], Number(e.target.value))} /></td>
                      <td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={y.percent ?? ''} onChange={e => updateSectionField('fha_closing_costs',['mortgage_insurance','years',i,'percent'], Number(e.target.value))} /></td>
                    </tr>
                  )) : null}
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="MMI Factor Setting" onSave={() => saveSection('fha_closing_costs', ['mmi_factor_setting'], 'MMI Factor Setting')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  {Array.isArray(data.fha_closing_costs.mmi_factor_setting) && data.fha_closing_costs.mmi_factor_setting.map((m, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 border">Factor</td>
                      <td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={m.factor ?? ''} onChange={e => updateSectionField('fha_closing_costs',['mmi_factor_setting',i,'factor'], Number(e.target.value))} /></td>
                      <td className="px-3 py-2 border">Up To</td>
                      <td className="px-3 py-2 border input-cell"><input className="w-36 border rounded px-2 py-1 text-sm ml-2" value={m.up_to_amount ?? m.exceeding_from_amount ?? ''} onChange={e => updateSectionField('fha_closing_costs',['mmi_factor_setting',i,'up_to_amount'], Number(e.target.value))} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="VA Loan Amount Factor" onSave={() => saveSection('va_closing_costs', ['loan_amount_factor'], 'VA Loan Amount Factor')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  <tr className="bg-white"><td className="px-3 py-2 border">Maximum 100% VA Loan</td><td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={data.va_closing_costs.loan_amount_factor.maximum_100_percent_va_loan ?? ''} onChange={e => updateSectionField('va_closing_costs',['loan_amount_factor','maximum_100_percent_va_loan'], Number(e.target.value))} /></td></tr>
                                    <tr className="bg-gray-50"><td className="px-3 py-2 border">Origination Factor %</td><td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={data.va_closing_costs.loan_amount_factor.origination_factor_percent ?? ''} onChange={e => updateSectionField('va_closing_costs',['loan_amount_factor','origination_factor_percent'], Number(e.target.value))} /></td></tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="VA Mortgage Insurance" onSave={() => saveSection('va_closing_costs', ['mortgage_insurance'], 'VA Mortgage Insurance')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  <tr className="bg-white"><td className="px-3 py-2 border">Funding Fee %</td><td className="px-3 py-2 border input-cell"><input className="w-full border rounded px-2 py-1 text-sm" value={data.va_closing_costs.mortgage_insurance.funding_fee_percent ?? ''} onChange={e => updateSectionField('va_closing_costs',['mortgage_insurance','funding_fee_percent'], Number(e.target.value))} /></td></tr>
                  <tr className="bg-gray-50"><td className="px-3 py-2 border">Finance MIP</td><td className="px-3 py-2 border"><input type="checkbox" checked={!!data.va_closing_costs.mortgage_insurance.finance_mip} onChange={e=> updateSectionField('va_closing_costs',['mortgage_insurance','finance_mip'], !!e.target.checked)} /></td></tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="USDA Loan Amount Factor" onSave={() => saveSection('usda_closing_costs', ['loan_amount_factor'], 'USDA Loan Amount Factor')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  {Object.entries(data.usda_closing_costs.loan_amount_factor).map(([k,v]) => (
                    <tr key={k} className="bg-white">
                      <td className="px-3 py-2 border">{formatLabel(k)}</td>
                      <td className="px-3 py-2 border input-cell">
                        {typeof v === 'boolean' ? (
                          <input type="checkbox" checked={!!v} onChange={e => updateSectionField('usda_closing_costs',['loan_amount_factor',k], !!e.target.checked)} />
                        ) : (
                          <input type={typeof v === 'number' ? 'number' : 'text'} className="w-full border rounded px-2 py-1 text-sm" value={v ?? ''} onChange={e => updateSectionField('usda_closing_costs',['loan_amount_factor',k], (typeof v === 'number' ? Number(e.target.value) : e.target.value))} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          </>
        ) : (
          <>
            <SectionCard title="Private Mortgage Insurance[P.M.I] Rates For 15 Year" onSave={() => saveSection('conventional_loans', ['pmi_rates_15_year'], 'PMI Rates 15 Year')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  {Object.entries(data.conventional_loans.pmi_rates_15_year.renewal_rates_percent).map(([k,v]) => (
                    <tr key={k} className="bg-white">
                      <td className="px-3 py-2 border">Renewal Rate for {formatLabel(k)} Loans</td>
                      <td className="px-3 py-2 border input-cell"><input type={typeof v === 'number' ? 'number' : 'text'} className="w-full border rounded px-2 py-1 text-sm" value={v ?? ''} onChange={e => updateSectionField('conventional_loans',['pmi_rates_15_year','renewal_rates_percent',k], Number(e.target.value))} /></td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50"><td className="px-3 py-2 border">Number of Months collected at closing</td><td className="px-3 py-2 border input-cell"><input className="w-24 border rounded px-2 py-1 text-sm" value={data.conventional_loans.pmi_rates_15_year.months_collected_at_closing ?? ''} onChange={e=> updateSectionField('conventional_loans',['pmi_rates_15_year','months_collected_at_closing'], Number(e.target.value))} /></td></tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="Private Mortgage Insurance[P.M.I] Rates For 30 Year" onSave={() => saveSection('conventional_loans', ['pmi_rates_30_year'], 'PMI Rates 30 Year')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  {Object.entries(data.conventional_loans.pmi_rates_30_year.renewal_rates_percent).map(([k,v]) => (
                    <tr key={k} className="bg-white">
                      <td className="px-3 py-2 border">Renewal Rate for {formatLabel(k)} Loans</td>
                      <td className="px-3 py-2 border input-cell"><input type={typeof v === 'number' ? 'number' : 'text'} className="w-full border rounded px-2 py-1 text-sm" value={v ?? ''} onChange={e => updateSectionField('conventional_loans',['pmi_rates_30_year','renewal_rates_percent',k], Number(e.target.value))} /></td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50"><td className="px-3 py-2 border">Number of Months collected at closing</td><td className="px-3 py-2 border input-cell"><input className="w-24 border rounded px-2 py-1 text-sm" value={data.conventional_loans.pmi_rates_30_year.months_collected_at_closing ?? ''} onChange={e=> updateSectionField('conventional_loans',['pmi_rates_30_year','months_collected_at_closing'], Number(e.target.value))} /></td></tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard title="Conventional Loan Defaults" onSave={() => saveSection('conventional_loans', ['conventional_loan_defaults'], 'Conventional Loan Defaults')}>
              <table className="w-full text-sm table-auto border-collapse">
                <tbody>
                  <tr className="bg-white"><td className="px-3 py-2 border">Resale Conventional Loan LTV%</td><td className="px-3 py-2 border input-cell"><input className="w-24 border rounded px-2 py-1 text-sm" value={data.conventional_loans.conventional_loan_defaults.resale_conventional_loan_ltv_percent ?? ''} onChange={e=> updateSectionField('conventional_loans',['conventional_loan_defaults','resale_conventional_loan_ltv_percent'], Number(e.target.value))} /></td></tr>
                  <tr className="bg-gray-50"><td className="px-3 py-2 border">Refinance Conventional Loan LTV%</td><td className="px-3 py-2 border input-cell"><input className="w-24 border rounded px-2 py-1 text-sm" value={data.conventional_loans.conventional_loan_defaults.refinance_conventional_loan_ltv_percent ?? ''} onChange={e=> updateSectionField('conventional_loans',['conventional_loan_defaults','refinance_conventional_loan_ltv_percent'], Number(e.target.value))} /></td></tr>
                </tbody>
              </table>
            </SectionCard>
          </>
        )}
      </div>

      {toast && <Toast message={toast.message} visible={true} />}
    </div>
  );
}
